import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, AreaChart, Area
} from 'recharts';
import { TrendingUp, Loader2, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTLShort = (n) => {
  const v = parseFloat(n) || 0;
  if (Math.abs(v) >= 1000000) return `₺${(v / 1000000).toFixed(1).replace('.0', '')}M`;
  if (Math.abs(v) >= 1000) return `₺${(v / 1000).toFixed(0)}K`;
  return `₺${v.toFixed(0)}`;
};
const formatTL = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-white/95 backdrop-blur px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }} className="tabular-nums">
          {p.name}: <strong>{formatTL(p.value)}</strong>
        </p>
      ))}
    </div>
  );
};

const RANGES = [3, 6, 12];

const DashboardCharts = () => {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analytics/monthly-trend`, { params: { months } })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [months]);

  const series = data?.series || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="dashboard-charts">
      <Card className="lg:col-span-2">
        <CardHeader className="border-b py-3 bg-slate-50/70">
          <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2 text-slate-800">
              <BarChart3 className="w-4 h-4 text-blue-600" /> Gelir / Gider Trendi
            </span>
            <div className="flex items-center gap-3">
              {data && (
                <span className="text-xs text-slate-500">
                  Gelir <strong className="text-emerald-600">{formatTLShort(data.total_income)}</strong> •
                  Gider <strong className="text-rose-600"> {formatTLShort(data.total_expense)}</strong> •
                  Net <strong className={data.total_net >= 0 ? 'text-blue-600' : 'text-rose-600'}> {formatTLShort(data.total_net)}</strong>
                </span>
              )}
              <div className="flex rounded-md overflow-hidden border" data-testid="chart-range-toggle">
                {RANGES.map(r => (
                  <Button
                    key={r}
                    size="sm"
                    variant="ghost"
                    onClick={() => setMonths(r)}
                    className={`h-7 px-2.5 rounded-none text-xs ${months === r ? 'bg-slate-900 text-white hover:bg-slate-800 hover:text-white' : 'text-slate-600'}`}
                    data-testid={`chart-range-${r}`}
                  >{r} Ay</Button>
                ))}
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          {loading && !data ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Grafik yükleniyor...
            </div>
          ) : series.length === 0 ? (
            <p className="h-64 flex items-center justify-center text-slate-400 text-sm">Grafik verisi yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} />
                <YAxis tickFormatter={formatTLShort} tick={{ fontSize: 11, fill: '#64748B' }} width={58} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Gelir" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={38} />
                <Bar dataKey="expense" name="Gider" fill="#E11D48" radius={[4, 4, 0, 0]} maxBarSize={38} />
                <Line dataKey="net" name="Net Kâr" stroke="#1D4ED8" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader className="border-b py-3 bg-slate-50/70">
          <CardTitle className="flex items-center gap-2 text-base text-slate-800">
            <TrendingUp className="w-4 h-4 text-emerald-600" /> Tahsilat Trendi
            <span className="text-xs font-normal text-slate-500">(hakem tahsilatı • müşteri tahsilatı • kapora)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          {series.length === 0 ? (
            <p className="h-56 flex items-center justify-center text-slate-400 text-sm">Grafik verisi yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gRef" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gCust" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gKap" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} />
                <YAxis tickFormatter={formatTLShort} tick={{ fontSize: 11, fill: '#64748B' }} width={58} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area dataKey="referee_collected" name="Hakem Tahsilatı" stroke="#F59E0B" fill="url(#gRef)" strokeWidth={2} stackId="1" />
                <Area dataKey="customer_payment" name="Müşteri Tahsilatı" stroke="#0EA5E9" fill="url(#gCust)" strokeWidth={2} stackId="1" />
                <Area dataKey="kapora" name="Kapora" stroke="#10B981" fill="url(#gKap)" strokeWidth={2} stackId="1" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardCharts;
